import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Toast, type ToastProps } from '@/components/ui/toast';

type ToastKind = NonNullable<ToastProps['kind']>;

interface ToastInput {
  kind?: ToastKind;
  title: ReactNode;
  message?: ReactNode;
  durationMs?: number;
}
interface ToastItem extends ToastInput {
  id: string;
}
interface ToastApi {
  toast: (t: ToastInput) => string;
  dismiss: (id: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);
const DEFAULT_MS = 5000;
// Module-local monotonic id counter. Avoids Date.now()/Math.random() (banned in
// some contexts) and is deterministic under fake timers in tests.
let seq = 0;

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const arm = useCallback(
    (id: string, ms: number) => {
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), ms),
      );
    },
    [dismiss],
  );

  const toast = useCallback(
    (t: ToastInput) => {
      const id = `t${++seq}`;
      setItems((prev) => [...prev, { ...t, id }]);
      arm(id, t.durationMs ?? DEFAULT_MS);
      return id;
    },
    [arm],
  );

  // pause-on-hover: clear the timer on enter, re-arm a fresh full duration on leave.
  const onEnter = (id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  };
  const onLeave = (it: ToastItem) => {
    if (!timers.current.has(it.id)) arm(it.id, it.durationMs ?? DEFAULT_MS);
  };

  return (
    <Ctx.Provider value={{ toast, dismiss }}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[1200] flex flex-col items-end gap-2"
        aria-live="polite"
      >
        {items.map((it) => (
          <div
            key={it.id}
            className="pointer-events-auto [animation:ops-toast-in_.18s_ease]"
            onMouseEnter={() => onEnter(it.id)}
            onMouseLeave={() => onLeave(it)}
          >
            <Toast
              kind={it.kind}
              title={it.title}
              message={it.message}
              onClose={() => dismiss(it.id)}
              role={it.kind === 'error' ? 'alert' : 'status'}
            />
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
